import type { Debt, SimulationResult, MonthlySnapshot, StrategyName } from '@/types'
import { MAX_SIMULATION_MONTHS } from '@/lib/constants'
import { monthlyInterest } from './interestCalc'
import { sortByStrategy, type DebtWithEffectiveAPR } from './strategies'
import { effectiveAPR } from './promoWatcher'
import { SIMULATION_CLEARED_THRESHOLD } from './tolerances'

export function runSimulation(
  debts: Debt[],
  totalMonthlyBudget: number,
  strategy: StrategyName,
  startDate: Date = new Date()
): SimulationResult {
  // Only optimize debts in the engine with a positive balance
  const eligible = debts.filter((d) => d.isInOptimizationEngine && d.balance > 0)

  if (eligible.length === 0) {
    return {
      strategy,
      monthsToPayoff: 0,
      totalInterestPaid: 0,
      totalAmountPaid: 0,
      attackOrder: [],
      monthlySnapshots: [],
      didConverge: true,
    }
  }

  // Deep-copy balances so we don't mutate the source
  const balances: Record<string, number> = {}
  for (const d of eligible) {
    balances[d.id] = d.balance
  }

  let cumulativeInterest = 0
  let cumulativePayments = 0
  const snapshots: MonthlySnapshot[] = []

  const attackOrderSet = new Set<string>()

  for (let month = 0; month < MAX_SIMULATION_MONTHS; month++) {
    // 1. Check total remaining balance
    const totalBalance = Object.values(balances).reduce((s, b) => s + b, 0)
    if (totalBalance < SIMULATION_CLEARED_THRESHOLD) break

    // 2. Build working list with effective APRs for this month.
    // `balances[d.id]` is guaranteed defined for every `eligible` debt by the
    // init loop above, but `noUncheckedIndexedAccess` widens the lookup to
    // `number | undefined`. The `?? 0` is purely a type-system narrowing — if
    // the invariant ever breaks, a missing debt simply contributes 0.
    const working: DebtWithEffectiveAPR[] = eligible
      .filter((d) => (balances[d.id] ?? 0) > 0)
      .map((d) => ({
        ...d,
        balance: balances[d.id] ?? 0,
        _effectiveAPR: effectiveAPR(d, month, startDate),
      }))

    // 3. Calculate and apply monthly interest to each debt
    let monthlyInterestTotal = 0
    for (const d of working) {
      const bal = balances[d.id] ?? 0
      const interest = monthlyInterest(bal, d._effectiveAPR)
      balances[d.id] = bal + interest
      monthlyInterestTotal += interest
    }
    cumulativeInterest += monthlyInterestTotal

    // 4. Pay minimums on all debts first
    let remainingBudget = totalMonthlyBudget
    const minimumsPaid: Record<string, number> = {}

    for (const d of working) {
      const bal = balances[d.id] ?? 0
      const minPay = Math.min(d.minimumPayment, bal)
      minimumsPaid[d.id] = minPay
    }

    const totalMinimumsNeeded = Object.values(minimumsPaid).reduce((s, p) => s + p, 0)

    // If budget can't cover minimums, pay proportionally (defensive)
    if (remainingBudget < totalMinimumsNeeded) {
      const ratio = remainingBudget / totalMinimumsNeeded
      for (const d of working) {
        const paid = (minimumsPaid[d.id] ?? 0) * ratio
        balances[d.id] = Math.max(0, (balances[d.id] ?? 0) - paid)
        cumulativePayments += paid
      }
      remainingBudget = 0
    } else {
      for (const d of working) {
        const min = minimumsPaid[d.id] ?? 0
        balances[d.id] = Math.max(0, (balances[d.id] ?? 0) - min)
        cumulativePayments += min
        remainingBudget -= min
      }
    }

    // 5. For non-minimum-only strategies, apply extra budget to priority debt(s)
    if (strategy !== 'minimum_only' && remainingBudget > SIMULATION_CLEARED_THRESHOLD) {
      const sorted = sortByStrategy(
        working.map((d) => ({ ...d, balance: balances[d.id] ?? 0 })).filter((d) => d.balance > 0),
        strategy
      )

      for (const target of sorted) {
        if (remainingBudget <= SIMULATION_CLEARED_THRESHOLD) break
        if (attackOrderSet.size === 0 || !attackOrderSet.has(target.id)) {
          attackOrderSet.add(target.id)
        }
        const targetBal = balances[target.id] ?? 0
        const extra = Math.min(remainingBudget, targetBal)
        balances[target.id] = Math.max(0, targetBal - extra)
        cumulativePayments += extra
        remainingBudget -= extra
      }
    }

    // 6. Record snapshot
    // v0.8.57 — day-1 anchor: setMonth on the 29th–31st overflowed (Oct 31
    // + 1 → Dec 1) and toISOString shifted to UTC, mislabelling months.
    const snapshotDate = new Date(startDate.getFullYear(), startDate.getMonth() + month + 1, 1)

    const newTotalBalance = Object.values(balances).reduce((s, b) => s + b, 0)
    snapshots.push({
      month: month + 1,
      date: `${snapshotDate.getFullYear()}-${String(snapshotDate.getMonth() + 1).padStart(2, '0')}`, // YYYY-MM, local
      totalBalance: Math.max(0, newTotalBalance),
      cumulativeInterestPaid: cumulativeInterest,
      debtBalances: { ...balances },
    })

    if (newTotalBalance < SIMULATION_CLEARED_THRESHOLD) break
  }

  const finalBalance = Object.values(balances).reduce((s, b) => s + b, 0)
  const didConverge = finalBalance < 1

  return {
    strategy,
    monthsToPayoff: didConverge ? snapshots.length : Infinity,
    totalInterestPaid: cumulativeInterest,
    totalAmountPaid: cumulativePayments,
    attackOrder: strategy === 'minimum_only' ? [] : Array.from(attackOrderSet),
    monthlySnapshots: snapshots,
    didConverge,
  }
}

export function runAllSimulations(
  debts: Debt[],
  extraMonthlyPayment: number,
  startDate: Date = new Date()
): {
  avalanche: SimulationResult
  snowball: SimulationResult
  hybrid: SimulationResult
  minimum_only: SimulationResult
  totalMonthlyBudget: number
} {
  const eligible = debts.filter((d) => d.isInOptimizationEngine && d.balance > 0)
  const totalMinimums = eligible.reduce((s, d) => s + d.minimumPayment, 0)
  const totalMonthlyBudget = totalMinimums + extraMonthlyPayment

  return {
    avalanche: runSimulation(eligible, totalMonthlyBudget, 'avalanche', startDate),
    snowball: runSimulation(eligible, totalMonthlyBudget, 'snowball', startDate),
    hybrid: runSimulation(eligible, totalMonthlyBudget, 'hybrid', startDate),
    minimum_only: runSimulation(eligible, totalMinimums, 'minimum_only', startDate),
    totalMonthlyBudget,
  }
}
